import { Button, Stack, Typography } from '@mui/material';
import { atom, useAtomValue } from 'jotai';
import { cloneDeep } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { GenericFallbacksWrapper } from '../../../../../components/Fallback/GenericFallbacksWrapper';
import { CompanyLogoAndName } from '../../../../../components/grid-renderers/CompanyCellRenderer';
import { Loader } from '../../../../../components/Loader/Loader';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { KPIRequestStatus } from '../../../../../data-models/kpi-requests.data-model';
import { LoadingStatus } from '../../../../../types';
import { useAsync } from '../../../../../util/hook-utils';
import { ConfirmDeleteRequest } from '../components/ConfirmDeleteRequest';
import {
  KPIReviewState,
  loadReviewerViewInitialData,
  ReviewerViewData,
  useKPIReviewerActions,
} from './hooks/useKPIRequestReviewActions';
import { KPIRenderedTemplate } from './KPIRenderedTemplate';
import { KpiResponseStatus } from './KpiResponseStatus';
import { KPIReviewHeader } from './KPIReviewHeader';
import {
  Background,
  headerWrapperCSS,
  headerWrapperInnerCSS,
  kpiContentsInnerWrapperCss,
  kpiContentsWrapperCss,
  mainContentWrapperCss,
  statusWrapperCss,
} from './KpiViewCommonStyles';
import { useShouldShowManagedServicesKpiGrid } from './managed-services-utils';
import { ManagedServiceKpiGrid } from './ManagedServiceKpiGrid';
import { ReviewResponseDialog } from './ReviewResponseDialog';

interface IKpiReviewerViewProps {
  reqId?: number;
  simplifiedView?: boolean;
}

export function KPIReviewerView({ reqId, simplifiedView = false }: IKpiReviewerViewProps) {
  const params = useParams<{ requestId: string }>();
  const requestId = reqId ?? params.requestId;
  const { error, loading, data } = useAsync(
    useCallback(() => loadReviewerViewInitialData(Number(requestId)), [requestId])
  );

  switch (loading) {
    case LoadingStatus.loading:
      return <Loader />;

    case LoadingStatus.error:
      return <Typography>{error}</Typography>;

    case LoadingStatus.success:
    default:
      if (data?.archived) {
        return <ArchivedRequestNotice company={data.company} />;
      }
      return <Layout initialData={data} simplifiedView={simplifiedView} />;
  }
}

export function ArchivedRequestNotice({
  company,
  showDeletedRequestsLink = true,
}: {
  company: ICompanyDataModel;
  showDeletedRequestsLink?: boolean;
}) {
  return (
    <Background>
      <Stack alignItems='center' paddingTop='6rem' gap='1rem'>
        <CompanyLogoAndName
          name={company.name}
          logoUrl={company.logoUrl ?? ''}
          typographyProps={{ fontSize: '26px' }}
          avatarProps={{ size: 'large' }}
        />
        <Typography variant='body1' color='text.secondary'>
          This request has been deleted. Its response is hidden but not permanently removed.
        </Typography>
        {showDeletedRequestsLink && (
          <Button
            component={Link}
            to={`/${ROUTES.KPI}/${ROUTES.KPI_REQUESTS}/${ROUTES.KPI_REQUESTS_ARCHIVED}`}
            variant='outlined'
            color='secondary'
          >
            Go to Deleted Requests
          </Button>
        )}
      </Stack>
    </Background>
  );
}

type LayoutProps = {
  initialData: ReviewerViewData;
  simplifiedView?: boolean;
};

function Layout(props: LayoutProps) {
  const componentState = useMemo(
    () =>
      atom<KPIReviewState>({
        ...cloneDeep(props.initialData),
        isModified: false,
        isSubmitting: false,
        submissionError: null,
      }),
    [props.initialData]
  );
  const { currentRequest, currentResponse, currentTemplate, isModified, isSubmitting } =
    useAtomValue(componentState);

  const {
    onSectionDataUpdated,
    onKPIGridDataUpdated,
    onTextualKpiUpdated,
    rejectResponse,
    acceptResponse,
    moveBackToReview,
    onDeleteRequest,
  } = useKPIReviewerActions(componentState);

  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const handleAccept = useCallback(
    async (comment?: string) => {
      await acceptResponse(comment);
      setShowAcceptDialog(false);
    },
    [acceptResponse]
  );

  const handleReject = useCallback(
    async (comment?: string) => {
      await rejectResponse(comment);
      setShowRejectDialog(false);
    },
    [rejectResponse]
  );

  const shouldShowManagedServiceKpiGrid = useShouldShowManagedServicesKpiGrid(
    currentRequest.status,
    currentTemplate
  );

  const readOnly =
    currentRequest.status === KPIRequestStatus.Accepted ||
    currentRequest.status === KPIRequestStatus.Rejected ||
    currentRequest.status === KPIRequestStatus.Archived;

  return (
    <Background>
      {showAcceptDialog && (
        <ReviewResponseDialog
          company={props.initialData.company}
          request={currentRequest}
          onSubmit={handleAccept}
          onCancel={() => setShowAcceptDialog(false)}
          action='accept'
        />
      )}
      {showRejectDialog && (
        <ReviewResponseDialog
          company={props.initialData.company}
          request={currentRequest}
          onSubmit={handleReject}
          onCancel={() => setShowRejectDialog(false)}
          action='reject'
        />
      )}
      <div css={mainContentWrapperCss}>
        {!props.simplifiedView && (
          <div css={headerWrapperCSS}>
            <div css={headerWrapperInnerCSS}>
              <KPIReviewHeader
                company={props.initialData.company}
                isSubmitting={isSubmitting}
                kpiRequest={currentRequest}
                onAcceptResponse={() => setShowAcceptDialog(true)}
                onRejectResponse={() => setShowRejectDialog(true)}
                onMoveBackToReview={moveBackToReview}
                onDeleteRequest={onDeleteRequest}
                isModified={isModified}
                submittedBy={currentResponse?.submittedBy}
                respondedAt={currentResponse?.respondedAt}
              />
              <div css={statusWrapperCss} key={currentRequest?.status}>
                <KpiResponseStatus
                  status={currentRequest.status}
                  acceptMessage={currentRequest.acceptMessage}
                  rejectReason={currentRequest.rejectReason}
                />
              </div>
            </div>
          </div>
        )}
        <div css={kpiContentsWrapperCss} className={props.simplifiedView ? 'small-screen' : ''}>
          <div css={kpiContentsInnerWrapperCss} className={'inner-wrapper'}>
            <GenericFallbacksWrapper>
              {currentTemplate ? (
                <KPIRenderedTemplate
                  onKPIGridDataUpdated={onKPIGridDataUpdated}
                  onSectionDataUpdated={onSectionDataUpdated}
                  onTextualKpiUpdated={onTextualKpiUpdated}
                  request={currentRequest}
                  response={currentResponse}
                  template={currentTemplate}
                  disabled={readOnly}
                />
              ) : (
                <Typography>Invalid Template</Typography>
              )}
              {shouldShowManagedServiceKpiGrid && <ManagedServiceKpiGrid request={currentRequest} />}
            </GenericFallbacksWrapper>
          </div>
        </div>
      </div>

      <ConfirmDeleteRequest />
    </Background>
  );
}
